import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Globe,
  CreditCard,
  Shield,
  Save,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { allCountries, countryNames } from '../lib/utils';

export default function SettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
    full_address: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        country: user.country || '',
        full_address: user.full_address || ''
      });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Note: Cette fonctionnalité nécessite un endpoint API pour mettre à jour le profil
    // Pour l'instant, on affiche juste un message
    toast.info('Fonctionnalité de mise à jour en cours de développement');
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="settings-page">
      <Navbar />
      
      <main className="pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-3xl text-slate-900">
              Paramètres du compte
            </h1>
            <p className="text-slate-600 mt-1">Gérez vos informations personnelles</p>
          </div>

          {/* Profile Info */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Informations personnelles
              </CardTitle>
              <CardDescription>
                Ces informations sont utilisées pour vous identifier et livrer vos colis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Number - Read only */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Numéro client</p>
                      <p className="font-heading font-bold text-2xl text-blue-700">{user?.customer_number}</p>
                    </div>
                    <Shield className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Ce numéro est unique et ne peut pas être modifié</p>
                </div>

                {/* Email - Read only */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Email
                  </Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="bg-slate-50 h-12"
                  />
                  <p className="text-xs text-slate-500">L'email ne peut pas être modifié</p>
                </div>

                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    Téléphone
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+33 6 12 34 56 78"
                    className="h-12"
                  />
                </div>

                {/* Country */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" />
                    Pays de destination
                  </Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(value) => handleChange('country', value)}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Sélectionnez votre pays" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCountries.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    Adresse de livraison complète
                  </Label>
                  <Input
                    value={formData.full_address}
                    onChange={(e) => handleChange('full_address', e.target.value)}
                    placeholder="123 Rue Example, 75001 Paris"
                    className="h-12"
                  />
                  <p className="text-xs text-slate-500">Adresse où vos colis seront réexpédiés</p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Moyens de paiement
              </CardTitle>
              <CardDescription>
                Vos paiements sont sécurisés par Stripe
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-slate-50 rounded-lg text-center">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  Paiement à la demande
                </h3>
                <p className="text-slate-600 mb-4">
                  Vous payez directement lors de la validation de l'expédition de chaque colis. 
                  Nous acceptons les cartes bancaires, Apple Pay et Google Pay.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200">
                    <CreditCard className="w-5 h-5 text-slate-600" />
                    <span className="text-sm font-medium">Carte bancaire</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm font-medium">Apple Pay</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200">
                    <span className="text-sm font-medium">Google Pay</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
